/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import { retrieve, store } from './storage_helper';
import APIHelper from "../api/APIHelper";
import envConfig from "../config/envConfig";

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

/**
 * Safely convert any field value to a display string.
 *
 * Guards against objects rendering as "[object Object]" anywhere in the app:
 * use this at every generic/fallback render sink instead of String(value) or
 * value.toString(). Type-specific renderers (image, fk, etc.) can still special-case
 * before falling back here.
 *
 * - null/undefined  -> "" (callers decide on a placeholder like "-")
 * - geo_point shape -> "lat, lng"
 * - arrays/objects  -> JSON string (never "[object Object]")
 * - everything else -> String(value)
 *
 * @param {*} value
 * @returns {string}
 */
export function toDisplayString(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") {
    // geo_point: { lat, lng } (or latitude/longitude)
    const lat = value.lat ?? value.latitude;
    const lng = value.lng ?? value.longitude;
    if (lat !== undefined || lng !== undefined) {
      return `${lat ?? ""}, ${lng ?? ""}`;
    }
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

/**
 * Handles Error response

/**
 * Handles Error response
 * @param {resp} resp 
 */
export async function handleErrorResponse(resp) {
  console.log(resp.status)
  if (resp.status === 400 || resp.status === 404) {
    const errorJson = await resp.json();
    const combinedErrorMessages = Object.entries(errorJson)
      .map(([field, messages]) => {
        if (Array.isArray(messages)) {
          return `${field}: ${messages.join(", ")}`;
        }
        return `${field}: ${String(messages)}`;
      })
      .join("\n");
    throw new Error(combinedErrorMessages);
  } else {
    throw new Error("An unexpected error occurred. Please refresh or try again later.");
  }
}

/**
 * Converts a UTC datetime string (e.g., "2025-12-01T18:29:00")
 * into the user's local timezone and formats it as:
 *   YYYY-MM-DD HH:mm:ss
 *
 * Example:
 *   Input  (UTC): "2025-12-01T18:29:00"
 *   Output (IST): "2025-12-01 23:59:00"
 *
 * This is used only for UI display. Backend always stores UTC.
 *
 * @param {string} utcString - UTC datetime string
 * @returns {string} Local datetime in YYYY-MM-DD HH:mm:ss format
 */
export function convertUTCStringToLocalStandard(utcString) {
  if (!utcString) return "";

  const utc = utcString.endsWith("Z") ? utcString : utcString + "Z";

  const d = new Date(utc);
  if (isNaN(d.getTime())) return utcString;

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const seconds = String(d.getSeconds()).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

/**
 * Converts a local datetime string back to a UTC ISO string.
 *
 * This is the inverse of convertUTCStringToLocalStandard().
 * Used before sending datetime values to the backend so that
 * values displayed in local time are re-converted to UTC.
 *
 * Example (IST → UTC):
 *   Input  (local): "2025-12-02 00:59:00"
 *   Output (UTC):   "2025-12-01T19:29:00.000Z"
 *
 * @param {string} localString - Local datetime string (YYYY-MM-DD HH:mm:ss or ISO)
 * @returns {string} UTC ISO string, or the original value if parsing fails
 */
export function convertLocalStringToUTCISO(localString) {
  if (!localString) return localString;

  const d = new Date(localString);
  if (isNaN(d.getTime())) return localString;

  return d.toISOString();
}

/**
 * Reads a stored datetime as the instant it actually names.
 *
 * A datetime column is `timestamp without time zone` holding UTC, and the API
 * serialises it with no offset — "2026-09-06T02:38:00". JS parses an
 * offset-less date-time as LOCAL, so a bare `new Date(...)` silently relabels a
 * UTC clock reading as the viewer's own: 02:38 UTC displayed as "2:38" to a
 * reader in IST, four and a half hours before the event it describes. The `+ Z`
 * here is the same one convertUTCStringToLocalStandard above already applies —
 * this variant returns the Date rather than a formatted string, and leaves a
 * value that already names its own zone alone.
 *
 * A date-only string names a day, not an instant, so it is rebuilt as LOCAL
 * midnight: `new Date('2026-07-28')` is UTC midnight, which renders as the 27th
 * for any viewer behind UTC.
 *
 * @param {string|Date} value
 * @returns {Date|null} null when there is nothing usable to parse
 */
export function parseInstant(value) {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

  const raw = String(value).trim();
  if (raw === "") return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split("-").map(Number);
    const local = new Date(y, (m || 1) - 1, d || 1);
    return isNaN(local.getTime()) ? null : local;
  }

  const text = raw.replace(" ", "T");
  const instant = new Date(/(?:Z|[+-]\d{2}:?\d{2})$/i.test(text) ? text : `${text}Z`);
  return isNaN(instant.getTime()) ? null : instant;
}

/**
 * The timezone a record declares for its own datetimes, or undefined.
 *
 * An event happens somewhere: a race starting 08:08 in Bengaluru starts at
 * 08:08 for everyone reading about it, not at 03:38 because the reader is in
 * London. A table that stores a `timezone` column is saying its times belong to
 * a place, and that is the whole signal needed — no table whitelist. A record
 * without one keeps the viewer's zone, which is right for a created_at.
 *
 * @param {object} record
 * @returns {string|undefined}
 */
export function zoneOf(record) {
  const zone = record && typeof record === "object" ? record.timezone : undefined;
  return typeof zone === "string" && zone.trim() !== "" ? zone.trim() : undefined;
}

/**
 * Runs a toLocale* formatter in `timeZone`, falling back to the viewer's zone.
 *
 * An unrecognised zone throws RangeError, which must never blank a public page
 * over one bad row — a failure degrades to the behaviour that applied before
 * zones were honoured at all.
 */
function inZone(date, method, options, timeZone) {
  // parseInstant returns null for a value it cannot read. Callers pass its
  // result straight in, and a blank cell is a better answer than a thrown
  // render — the whole public page would go down over one malformed row.
  if (!date) return "";
  if (timeZone) {
    try {
      return date[method](undefined, { ...options, timeZone });
    } catch {
      /* unusable zone — fall through to the viewer's own */
    }
  }
  return date[method](undefined, options);
}

/** Calendar parts of `date` as they read in `timeZone`. */
function partsIn(date, timeZone) {
  const local = {
    year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate(),
  };
  if (!timeZone) return local;
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone, year: "numeric", month: "2-digit", day: "2-digit",
    }).formatToParts(date);
    const out = {};
    for (const part of parts) {
      if (part.type === "year") out.year = Number(part.value);
      else if (part.type === "month") out.month = Number(part.value);
      else if (part.type === "day") out.day = Number(part.value);
    }
    return (out.year && out.month && out.day) ? out : local;
  } catch {
    return local;
  }
}

const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/**
 * Formats `date` against a token pattern ("d M Y"), read in `timeZone`.
 *
 * Longest tokens first so "MM" is never consumed as two "M"s. No short month
 * name contains a "d", so the day pass cannot corrupt one.
 */
export function formatInstantDate(date, pattern, timeZone) {
  const { year, month, day } = partsIn(date, timeZone);
  return String(pattern || "d M Y")
    .replace(/YYYY|Y/g, String(year))
    .replace(/MM/g, String(month).padStart(2, "0"))
    .replace(/M/g, MONTHS_SHORT[month - 1])
    .replace(/dd|d/g, String(day).padStart(2, "0"));
}

/**
 * "8:08 am" as it reads in `timeZone`.
 *
 * `hour12` is forced rather than left to the viewer's locale. Half the English
 * locales — en-GB among them — resolve to a 24-hour cycle, so the same event
 * rendered "0:20" for one reader and "12:20 am" for another, and a bare "0:20"
 * beside a date reads as a typo more than as midnight. A published event time
 * should not change shape with the browser's region.
 */
export function formatInstantTime(date, timeZone) {
  return inZone(
    date, "toLocaleTimeString",
    { hour: "numeric", minute: "2-digit", hour12: true },
    timeZone,
  );
}

/**
 * The short zone label for an instant — "IST", "BST", "EDT", or a "GMT+5:30"
 * offset when the zone genuinely has no common abbreviation.
 *
 * No single locale abbreviates every zone: en-IN says "IST" where en-US says
 * "GMT+5:30"; en-GB says "BST" where both others say "GMT+1"; en-US says "EDT"
 * where the others say "GMT-4". Asking only the viewer's locale therefore makes
 * the SAME event read differently for two readers, which is the inconsistency
 * this whole change set exists to remove.
 *
 * So the viewer's own locale is asked first — they get the spelling they know —
 * and if it answers with a bare GMT offset we ask a few others and take the
 * first real abbreviation. No zone and no country is named here: the candidates
 * are locales, and which one happens to answer depends entirely on the zone
 * being formatted.
 *
 * Empty when there is no zone or the engine cannot resolve it, so a caller can
 * render nothing rather than a stray separator.
 */
const ZONE_NAME_LOCALES = [undefined, "en-IN", "en-GB", "en-US", "en-AU"];
const BARE_OFFSET_RE = /^(GMT|UTC)([+-]|$)/i;

export function zoneLabel(date, timeZone) {
  if (!date || !timeZone) return "";
  let fallback = "";
  for (const locale of ZONE_NAME_LOCALES) {
    let value;
    try {
      const part = new Intl.DateTimeFormat(locale, { timeZone, timeZoneName: "short" })
        .formatToParts(date)
        .find((p) => p.type === "timeZoneName");
      value = part ? part.value : "";
    } catch {
      // An unknown zone throws for every locale; an unknown LOCALE throws for
      // just this pass, so keep trying the rest before giving up.
      continue;
    }
    if (!value) continue;
    if (!BARE_OFFSET_RE.test(value)) return value;   // a real abbreviation
    if (!fallback) fallback = value;                 // remember the offset form
  }
  return fallback;
}

/** A locale date ("6 September 2026") as it reads in `timeZone`. */
export function localeDateIn(date, options, timeZone) {
  return inZone(date, "toLocaleDateString", options, timeZone);
}

// ── Session-check de-duplication ──────────────────────────────────────────────
//
// WHY THIS EXISTS. `/arcee/user/login/check` used to be a bare round trip with
// no cache and no dedupe, and several independent consumers ask the same
// question during a single page load. On the event detail page alone that was
// four identical requests — the contact prefill, the entry-marker check,
// `checkRegistration` inside it, and usePublicAction — doubled again to eight
// by StrictMode's double-invoked effects in development.
//
// Nothing here changes what a caller receives. Concurrent callers share the one
// in-flight promise, and a caller arriving within TTL_MS of a settled answer
// reuses it. Anything that must not be served a cached answer passes
// { fresh: true } (see the pre-write re-check in EventDetailPage).
const SESSION_TTL_MS = 8000;

// Keyed by the resolved application id, because the answer is scoped to it.
const _sessionInFlight = new Map();
const _sessionCache = new Map(); // appId -> { value, at }

/**
 * Forgets every cached session answer.
 *
 * Called on `arcee:auth-change` (signing in, signing out) so the next check
 * asks the server rather than reporting the previous session.
 */
export function clearSessionCache() {
  _sessionInFlight.clear();
  _sessionCache.clear();
}

if (typeof window !== "undefined") {
  window.addEventListener("arcee:auth-change", clearSessionCache);
}

/**
 * The real request. Kept separate so the cache layer stays readable.
 *
 * Returns { value, settled }. Callers still only ever see `value` — `settled`
 * decides whether that value may be REMEMBERED.
 *
 * SETTLED means the server answered the question: a 200 saying who this is, or
 * a 200 saying nobody. Both are facts about the visitor and are safe to cache.
 *
 * UNSETTLED means the question could not be asked. A token that expired between
 * two calls, a session the server rejected, a 500, a dropped connection — all
 * of them surface here as `null`, and all of them are indistinguishable from
 * "signed out" if you only look at the value.
 *
 * That distinction is the whole reason this shape exists. APIHelper answers a
 * 401 by refreshing the OAuth token, retrying once, and — if that still fails —
 * clearing the token and throwing. Caching the resulting `null` would take one
 * transient fault and turn it into eight seconds of telling a signed-in person
 * they are signed out, which is long enough to bounce them to /sign-in in the
 * middle of a registration they had already started.
 */
async function _fetchLoginCheck(appId) {
  try {
    const qs = appId ? `?application_id=${encodeURIComponent(appId)}` : "";
    const resp = await apiHelper.apiCall(`/arcee/user/login/check${qs}`, {
      method: "GET",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      suppressSessionEvent: true,
    });

    // Refused rather than answered — never remembered.
    if (!resp.ok) return { value: null, settled: false };

    try {
      const data = await resp.json();
      return { value: data || null, settled: true };
    } catch {
      // 200 with a body that is not JSON is a proxy or error page, not an
      // answer about this visitor.
      return { value: null, settled: false };
    }
  } catch (err) {
    // Thrown: oauth_token_invalid after a failed refresh, session_expired, or
    // the network. Never remembered.
    return { value: null, settled: false };
  }
}

/**
 * Checks if the user is logged in by calling the backend session validation API.
 *
 * Returns the user data object { user_name, first_name, last_name } when the session
 * is valid, or null when not logged in. Callers that only need a boolean should use !!result.
 *
 * Also accepts an optional applicationId to resolve the membership-scoped username.
 *
 * @param {string} [applicationId] - Optional application ID for membership username lookup.
 * @param {Object} [options]
 * @param {boolean} [options.fresh] - Bypass the cache and ask the server. Use
 *        this wherever a stale answer would let a write through that should
 *        have been refused.
 * @returns {Promise<object|null>} User data object if logged in, null otherwise.
 */
export async function isUserLoggedIn(applicationId, { fresh = false } = {}) {
  const appId = applicationId || envConfig.applicationId;
  const key = String(appId ?? "");

  if (fresh) {
    _sessionCache.delete(key);
    _sessionInFlight.delete(key);
  } else {
    const hit = _sessionCache.get(key);
    if (hit && Date.now() - hit.at < SESSION_TTL_MS) return hit.value;

    // Already asking. Join that request rather than opening a second one —
    // this is what collapses a page load's burst into one call.
    const pending = _sessionInFlight.get(key);
    if (pending) return pending;
  }

  const promise = _fetchLoginCheck(appId)
    .then(({ value, settled }) => {
      // Only a real answer is remembered. An unsettled result is returned to
      // this caller and then forgotten, so the very next caller re-asks and a
      // recovered token or session takes effect immediately instead of after
      // the TTL.
      if (settled) _sessionCache.set(key, { value, at: Date.now() });
      return value;
    })
    .finally(() => {
      _sessionInFlight.delete(key);
    });

  _sessionInFlight.set(key, promise);
  return promise;
}